import { decodeFrame, parseServerMessage, type ClientMessage, type ServerMessage } from '@ohmyobs/protocol';
import type { Logger } from '../core/services';
import { DEFAULT_BACKOFF, backoffDelay, type BackoffOptions } from './backoff';

/** The subset of WebSocket the client uses; lets tests substitute a scripted socket. */
export interface SocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((event: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code: number; reason: string }) => void) | null;
  onerror: ((event: unknown) => void) | null;
}

/**
 * `connecting`: a socket is being opened or the hello is unanswered.
 * `ready`: the server acknowledged the hello; queued messages have been flushed.
 * `waiting`: disconnected, next attempt scheduled.
 * `closed`: closed by the owner; terminal.
 */
export type SignalingStatus = 'connecting' | 'ready' | 'waiting' | 'closed';

export interface SignalingClientOptions {
  url: string;
  /** First message on every (re)connection: creates/reclaims the room or (re)joins it. */
  hello(): ClientMessage;
  onMessage(message: ServerMessage): void;
  /** `attempts` counts failed connections since the last time the link was ready. */
  onStatus(status: SignalingStatus, attempts: number): void;
  logger: Logger;
  scope: string;
  backoff?: BackoffOptions;
  createSocket?: (url: string) => SocketLike;
  pingIntervalMs?: number;
  /** A ping left unanswered (no traffic at all since it was sent) for this long means the link is dead. */
  deadAfterMs?: number;
  connectTimeoutMs?: number;
}

export interface SignalingClient {
  readonly status: SignalingStatus;
  /** Sent immediately when ready, otherwise queued and flushed after the next acknowledged hello. */
  send(message: ClientMessage): void;
  /** Call when the server has acknowledged the hello (`room-opened` / `joined`). */
  markReady(): void;
  /** Drop the current socket and go through the backoff path, e.g. after a refused hello. */
  retry(): void;
  /** Stop for good. `farewell` is sent first if the link is ready. */
  close(farewell?: ClientMessage): void;
}

/**
 * Failed connections tolerated before a first connect is reported as "cannot reach
 * the server". One refused or stalled handshake is not evidence of a wrong URL.
 */
export const INITIAL_CONNECT_ATTEMPTS = 3;

const SOCKET_OPEN = 1;
const MAX_QUEUED = 512;

export function createSignalingClient(options: SignalingClientOptions): SignalingClient {
  const {
    url,
    logger,
    scope,
    backoff = DEFAULT_BACKOFF,
    pingIntervalMs = 10_000,
    deadAfterMs = 25_000,
    connectTimeoutMs = 10_000,
  } = options;
  const createSocket = options.createSocket ?? ((u: string) => new WebSocket(u) as unknown as SocketLike);

  let status: SignalingStatus = 'connecting';
  let socket: SocketLike | null = null;
  let attempts = 0;
  let lastSeen = 0;
  /** When the oldest ping that has seen no traffic since was sent; null when nothing is outstanding. */
  let unansweredSince: number | null = null;
  let queue: ClientMessage[] = [];
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let connectTimer: ReturnType<typeof setTimeout> | null = null;
  let pingTimer: ReturnType<typeof setInterval> | null = null;

  function setStatus(next: SignalingStatus): void {
    status = next;
    options.onStatus(next, attempts);
  }

  function clearTimers(): void {
    if (retryTimer) clearTimeout(retryTimer);
    if (connectTimer) clearTimeout(connectTimer);
    if (pingTimer) clearInterval(pingTimer);
    retryTimer = connectTimer = pingTimer = null;
  }

  function detach(s: SocketLike): void {
    s.onopen = s.onmessage = s.onclose = s.onerror = null;
  }

  function sendNow(message: ClientMessage): void {
    if (socket && socket.readyState === SOCKET_OPEN) socket.send(JSON.stringify(message));
  }

  /** The current socket is finished (closed, errored or presumed dead): schedule the next attempt. */
  function lost(reason: string): void {
    if (status === 'closed') return;
    const s = socket;
    socket = null;
    clearTimers();
    if (s) {
      detach(s);
      try {
        s.close();
      } catch {
        // Already closed.
      }
    }
    const delay = backoffDelay(attempts, backoff);
    attempts += 1;
    logger.log('info', scope, `signalling link lost (${reason}); retry ${attempts} in ${delay} ms`);
    retryTimer = setTimeout(connect, delay);
    setStatus('waiting');
  }

  function connect(): void {
    if (status === 'closed') return;
    clearTimers();
    let s: SocketLike;
    try {
      s = createSocket(url);
    } catch (err) {
      logger.log('error', scope, 'could not create the signalling socket', err);
      lost('socket constructor threw');
      return;
    }
    socket = s;
    if (status !== 'connecting') setStatus('connecting');
    connectTimer = setTimeout(() => lost('connect timeout'), connectTimeoutMs);

    s.onopen = () => {
      if (connectTimer) clearTimeout(connectTimer);
      connectTimer = null;
      lastSeen = Date.now();
      unansweredSince = null;
      logger.log('debug', scope, 'signalling socket open; sending hello');
      sendNow(options.hello());
      pingTimer = setInterval(() => {
        // A hidden tab runs this once a second at best and once a minute at worst, so
        // "nothing heard for N seconds" proves nothing by itself: this page may simply not
        // have asked. Incoming frames are handled on time regardless of timer throttling, so
        // the link is only dead when a ping that was actually sent got no traffic back.
        const now = Date.now();
        if (unansweredSince !== null && lastSeen >= unansweredSince) unansweredSince = null;
        if (unansweredSince !== null && now - unansweredSince > deadAfterMs) {
          lost('no traffic from server');
          return;
        }
        unansweredSince ??= now;
        sendNow({ type: 'ping', t: now });
      }, pingIntervalMs);
    };
    s.onmessage = (event) => {
      lastSeen = Date.now();
      if (typeof event.data !== 'string') return;
      const parsed = decodeFrame(event.data, parseServerMessage);
      if (!parsed.ok) {
        logger.log('warn', scope, `ignored malformed signalling message: ${parsed.error}`);
        return;
      }
      if (parsed.value.type !== 'pong') options.onMessage(parsed.value);
    };
    s.onclose = (event) => lost(`closed ${event.code}`);
    // An error is always followed by close; handling both would schedule two retries.
    s.onerror = () => {};
  }

  // A network coming back, or the tab coming back to the foreground (where its backoff timer
  // may have been stretched to a minute), is a better retry signal than the tail of a backoff.
  const onOnline = (): void => {
    if (status === 'waiting') connect();
  };
  const onVisible = (): void => {
    if (status === 'waiting' && document.visibilityState === 'visible') connect();
  };
  const target: Pick<Window, 'addEventListener' | 'removeEventListener'> | null =
    typeof window !== 'undefined' ? window : null;
  const doc: Pick<Document, 'addEventListener' | 'removeEventListener'> | null =
    typeof document !== 'undefined' ? document : null;
  target?.addEventListener('online', onOnline);
  doc?.addEventListener('visibilitychange', onVisible);

  connect();

  return {
    get status() {
      return status;
    },
    send(message) {
      if (status === 'closed') return;
      if (status === 'ready') {
        sendNow(message);
        return;
      }
      if (queue.length >= MAX_QUEUED) queue.shift();
      queue.push(message);
    },
    markReady() {
      if (status === 'closed' || status === 'ready') return;
      attempts = 0;
      const pending = queue;
      queue = [];
      setStatus('ready');
      for (const message of pending) sendNow(message);
    },
    retry() {
      lost('retry requested');
    },
    close(farewell) {
      if (status === 'closed') return;
      if (farewell && status === 'ready') sendNow(farewell);
      clearTimers();
      target?.removeEventListener('online', onOnline);
      doc?.removeEventListener('visibilitychange', onVisible);
      queue = [];
      const s = socket;
      socket = null;
      status = 'closed';
      if (s) {
        detach(s);
        try {
          s.close(1000, 'client closed');
        } catch {
          // Already closed.
        }
      }
      options.onStatus('closed', attempts);
    },
  };
}
